import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import Loader from "../../Loader";
import { FUNNEL_EVENT_NAMES, FUNNEL_LABELS } from "./ProductAnalyticsWidget";

const GET_ANALYTICS_DROPOFF_USERS = gql`
  query getAnalyticsDropoffUsers(
    $fromEvent: String!
    $toEvent: String!
    $startDate: String!
    $endDate: String!
    $onlyUncontacted: Boolean
    $limit: Int
  ) {
    getAnalyticsDropoffUsers(
      fromEvent: $fromEvent
      toEvent: $toEvent
      startDate: $startDate
      endDate: $endDate
      onlyUncontacted: $onlyUncontacted
      limit: $limit
    ) {
      status
      message
      data {
        userId
        fullName
        contactNumber
        propertiesJson
        lastEventAt
        contacted
        notes
        contactedAt
        existingBookingNote
      }
    }
  }
`;

const SET_ANALYTICS_DROPOFF_CONTACT = gql`
  mutation setAnalyticsDropoffContact(
    $userId: String!
    $fromEvent: String!
    $toEvent: String!
    $contacted: Boolean!
    $notes: String
  ) {
    setAnalyticsDropoffContact(
      userId: $userId
      fromEvent: $fromEvent
      toEvent: $toEvent
      contacted: $contacted
      notes: $notes
    ) {
      status
      message
    }
  }
`;

const formatProperties = (propertiesJson) => {
  try {
    const parsed = JSON.parse(propertiesJson || "{}");
    const entries = Object.entries(parsed).filter(([key]) =>
      ["address", "cart_value", "amount", "duration_minutes"].includes(key),
    );
    if (entries.length === 0) return "—";
    return entries.map(([key, value]) => `${key}: ${value}`).join(", ");
  } catch (e) {
    return "—";
  }
};

const DropoffRow = ({ row, fromEvent, toEvent, onSaved }) => {
  const [contacted, setContacted] = useState(row.contacted);
  const [notes, setNotes] = useState(row.notes || "");
  const [dirty, setDirty] = useState(false);

  const [saveContact, { loading: saving }] = useMutation(SET_ANALYTICS_DROPOFF_CONTACT);

  const handleSave = async () => {
    await saveContact({
      variables: { userId: row.userId, fromEvent, toEvent, contacted, notes },
    });
    setDirty(false);
    onSaved();
  };

  return (
    <tr className="border-b border-gray-800 align-top">
      <td className="py-2 pr-4 whitespace-nowrap">{row.contactNumber || "—"}</td>
      <td className="py-2 pr-4">
        {row.fullName || "—"}
        {row.existingBookingNote && (
          <div className="mt-1 inline-block bg-amber-900/40 text-amber-300 text-[11px] px-2 py-0.5 rounded-md">
            {row.existingBookingNote}
          </div>
        )}
      </td>
      <td className="py-2 pr-4 text-xs text-gray-400 break-all">
        {formatProperties(row.propertiesJson)}
      </td>
      <td className="py-2 pr-4 whitespace-nowrap font-mono text-xs">
        {row.lastEventAt ? new Date(row.lastEventAt).toLocaleString() : "—"}
      </td>
      <td className="py-2 pr-4 text-center">
        <input
          type="checkbox"
          checked={contacted}
          onChange={(e) => {
            setContacted(e.target.checked);
            setDirty(true);
          }}
        />
      </td>
      <td className="py-2 pr-4">
        <input
          type="text"
          className="bg-gray-800 text-gray-200 text-xs rounded-md px-2 py-1 border border-gray-700 w-full"
          placeholder="Notes (e.g. call outcome)"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setDirty(true);
          }}
        />
      </td>
      <td className="py-2">
        <button
          disabled={!dirty || saving}
          onClick={handleSave}
          className="bg-[#FBB033] text-black text-xs font-semibold px-3 py-1 rounded-md hover:opacity-90 disabled:opacity-40"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </td>
    </tr>
  );
};

// "Who dropped off between step A and step B" — surfaces phone numbers so
// support/growth can call these users back, with a lightweight per-user
// contacted/notes tracker so the same person isn't called twice.
const DropoffExplorerWidget = ({ filters }) => {
  const [fromEvent, setFromEvent] = useState("cart.viewed");
  const [toEvent, setToEvent] = useState("booking.completed");
  const [onlyUncontacted, setOnlyUncontacted] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_ANALYTICS_DROPOFF_USERS, {
    variables: {
      fromEvent,
      toEvent,
      startDate: filters.startDate,
      endDate: filters.endDate,
      onlyUncontacted,
      limit: 200,
    },
    fetchPolicy: "network-only",
  });

  const rows = data?.getAnalyticsDropoffUsers?.data || [];

  return (
    <div className="bg-[#1C1C1E] p-6 rounded-2xl shadow-lg border border-gray-800">
      <h3 className="text-[#FBB033] font-bold text-xl mb-4">Drop-off Explorer</h3>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-gray-400 text-sm">Did</span>
        <select
          className="bg-gray-800 text-gray-200 text-sm rounded-md px-2 py-2 border border-gray-700"
          value={fromEvent}
          onChange={(e) => setFromEvent(e.target.value)}
        >
          {FUNNEL_EVENT_NAMES.map((name) => (
            <option key={name} value={name}>
              {FUNNEL_LABELS[name] || name}
            </option>
          ))}
        </select>
        <span className="text-gray-400 text-sm">but never</span>
        <select
          className="bg-gray-800 text-gray-200 text-sm rounded-md px-2 py-2 border border-gray-700"
          value={toEvent}
          onChange={(e) => setToEvent(e.target.value)}
        >
          {FUNNEL_EVENT_NAMES.map((name) => (
            <option key={name} value={name}>
              {FUNNEL_LABELS[name] || name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-400 ml-4">
          <input
            type="checkbox"
            checked={onlyUncontacted}
            onChange={(e) => setOnlyUncontacted(e.target.checked)}
          />
          Not yet contacted
        </label>
        <span className="text-gray-500 text-sm ml-auto">{rows.length} users</span>
      </div>

      {loading && <Loader />}
      {error && <div className="text-red-500">Error loading drop-off users</div>}

      {!loading && rows.length === 0 && (
        <div className="text-gray-500">No drop-off found for this combination.</div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-300">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="py-2 pr-4">Phone</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Last Known Context</th>
                <th className="py-2 pr-4">Last Activity</th>
                <th className="py-2 pr-4">Contacted</th>
                <th className="py-2 pr-4">Notes</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <DropoffRow
                  key={row.userId}
                  row={row}
                  fromEvent={fromEvent}
                  toEvent={toEvent}
                  onSaved={refetch}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DropoffExplorerWidget;
